import { useAtomValue } from 'jotai';
import { selectedBusinessUnitIdAtom } from '@/stores/business-unit.store';
import { useEffect, useState, useMemo } from 'react';
import { Loader2, Mail, ShieldAlert } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { toast } from '@/components/ui/sonner';
import { cn, getApiErrorMessage } from '@/lib/utils';
import { socialAgentService } from '@/services/social-agent.service';
import { useAuth } from '@/hooks/use-auth';
import { describeActionFilter, describeDecisionFilter, type ActionLogFilters, type ActionLogWindow } from '../action-log-constants';
import { formatLogTimestamp } from '../action-log-format';
import { DIALOG_SHELL, DIALOG_TITLE, FIELD, FIELD_HINT, FIELD_LABEL } from '../ui-chrome';

/**
 * Export — the filters, restated, and what leaves the building.
 *
 * The summary is not decoration. The export runs the query the SCREEN is showing, not the rows
 * loaded into it, so the only way a person can tell what they are about to receive is to be told
 * what the filters currently are.
 *
 * The file is emailed rather than downloaded. That makes the export an addressed delivery with a
 * record of who asked for it, instead of an anonymous stream of customer data into a browser.
 */

interface ActionLogExportDialogProps {
	open: boolean;
	onOpenChange: (open: boolean) => void;
	filters: ActionLogFilters;
	/** The same resolved window the rows were fetched with, so the CSV cannot answer a later question. */
	appliedWindow: ActionLogWindow;
	/** How many rows are on screen. Not the export size — the export is the whole filtered set. */
	loadedRowCount: number;
	/** True when the list has further pages, i.e. the export will contain more than is on screen. */
	hasMore: boolean;
}

const SummaryRow = ({ label, value }: { label: string; value: string }) => (
	<div className='flex items-start justify-between gap-6 py-2'>
		<span className='shrink-0 text-[13px] text-muted-foreground'>{label}</span>
		<span className='min-w-0 text-right text-[13px] text-foreground'>{value}</span>
	</div>
);

export const ActionLogExportDialog = ({
	open,
	onOpenChange,
	filters,
	appliedWindow,
	loadedRowCount,
	hasMore
}: ActionLogExportDialogProps) => {
	const { currentUser } = useAuth();
	const [submitting, setSubmitting] = useState(false);
	const [error, setError] = useState<string | null>(null);
	const [email, setEmail] = useState('');

	// A failure from a previous attempt is about a request that is over. Showing it again next time
	// the dialog opens would report an error against an export nobody has started yet.
	//
	// The address is reset alongside it, so the field always opens showing where the file will
	// actually go rather than an address left over from a previous send.
	useEffect(() => {
		if (open) {
			setError(null);
			setEmail(currentUser?.email ?? '');
		}
	}, [open, currentUser?.email]);

	// Deliberately shallow. Anything stricter rejects valid addresses, and the server is the only
	// place that finds out whether one is deliverable.
	const emailLooksValid = /^\S+@\S+\.\S+$/.test(email.trim());

	const rowCountSummary = hasMore
		? `${loadedRowCount} loaded so far — the file includes every matching row`
		: `${loadedRowCount} rows`;

	const selectedBusinessUnitId = useAtomValue(selectedBusinessUnitIdAtom);
	const buScope = useMemo(
		() => (selectedBusinessUnitId ? { bu_id: selectedBusinessUnitId } : {}),
		[selectedBusinessUnitId]
	);
	const requestExport = async () => {
		if (submitting || !emailLooksValid) return;
		setSubmitting(true);
		setError(null);

		const result = await socialAgentService
			.requestActionLogExport({ window: appliedWindow, filters }, email.trim(), buScope)
			.catch((err: unknown) => {
				console.error('[ActionLogExportDialog] Export request failed', { filters, appliedWindow, error: err });
				setError(getApiErrorMessage(err, 'Could not start the export. Please try again.'));
				return null;
			});

		setSubmitting(false);
		if (!result) return;

		// Deliberately not "Exported". The job has been accepted, not finished, and a success
		// message that implies delivery makes a mail failure look like a mail that has not arrived
		// yet — indefinitely.
		toast.success(
			result.email ? `Export on its way to ${result.email}` : 'Export started — it will arrive by email shortly'
		);

		onOpenChange(false);
	};

	return (
		<Dialog open={open} onOpenChange={onOpenChange}>
			{/* The email field is the first focusable thing in here, so Radix's opening focus landed
			    on it with the address already selected — one keystroke from replacing an address the
			    reader had not yet read. Nothing is focused on open, as in the rule dialog. */}
			<DialogContent
				showCloseButton={false}
				onOpenAutoFocus={(event) => event.preventDefault()}
				className={cn(DIALOG_SHELL, 'sm:max-w-[560px]')}
			>
				{/* Same design-system dialog shell as the rule and delay modals. */}
				<DialogHeader className='gap-1 border-b border-border px-3 py-2'>
					<DialogTitle className={DIALOG_TITLE}>Export action log</DialogTitle>
					<DialogDescription className={FIELD_HINT}>
						We'll email you the file. It contains every row matching the filters below, not only the
						rows on screen.
					</DialogDescription>
				</DialogHeader>

				<div className='flex flex-col px-4 py-6'>
					<div className='divide-y divide-gray-200'>
						<SummaryRow
							label='Date range'
							value={`${formatLogTimestamp(appliedWindow.from.toISOString())} – ${formatLogTimestamp(appliedWindow.to.toISOString())}`}
						/>
						<SummaryRow label='Decision' value={describeDecisionFilter(filters.decisions)} />
						<SummaryRow label='Action' value={describeActionFilter(filters.actionTypes)} />
						<SummaryRow
							label='Channel'
							value={filters.channelIds.length === 0 ? 'All channels' : `${filters.channelIds.length} selected`}
						/>
						{filters.search.trim().length > 0 && <SummaryRow label='Search' value={filters.search.trim()} />}
						<SummaryRow label='Rows' value={rowCountSummary} />
					</div>

					<div className='mt-6 flex flex-col gap-2'>
						<Label htmlFor='action-log-export-email' className={FIELD_LABEL}>
							Send to
						</Label>
						<Input
							id='action-log-export-email'
							type='email'
							value={email}
							onChange={(e) => setEmail(e.target.value)}
							placeholder='name@company.com'
							aria-invalid={email.trim().length > 0 && !emailLooksValid}
							className={FIELD}
						/>
						{email.trim().length > 0 && !emailLooksValid && (
							<p className={FIELD_HINT}>That does not look like an email address.</p>
						)}
					</div>

					{/*
					 * Said before the send, not after. The rows quote customers verbatim and name them,
					 * and the moment the file is in an inbox it is outside every control this product
					 * has.
					 */}
					<div className='mt-4 flex gap-2.5 rounded-xl bg-amber-50 p-3 text-[13px] text-amber-900'>
						<ShieldAlert className='mt-0.5 size-4 shrink-0' />
						<p>
							This export includes commenter handles and the full text of their comments — customer data
							from a public platform. It will be sent as an email attachment to the address above.
							Handle and store it accordingly.
						</p>
					</div>

					{error && <p className='pt-3 text-[13px] text-destructive'>{error}</p>}
				</div>

				<div className='flex justify-end gap-1.5 p-3'>
					<Button variant='outline' className='rounded-full' disabled={submitting} onClick={() => onOpenChange(false)}>
						Cancel
					</Button>
					<Button className='gap-2 rounded-full' disabled={submitting || !emailLooksValid} onClick={requestExport}>
						{submitting ? <Loader2 className='size-4 animate-spin' /> : <Mail className='size-4' />}
						Email me the CSV
					</Button>
				</div>
			</DialogContent>
		</Dialog>
	);
};
